import { ThemeSelect } from "@ai-crm/agent-ui";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useState } from "react";
import type { ReactNode } from "react";
import type { LucideIcon } from "lucide-react";
import {
  ArrowLeft,
  FileText,
  KeyRound,
  ListTree,
  LogOut,
  Menu,
  Shield,
  Tags,
  X,
} from "lucide-react";
import { useAdminAuth } from "../lib/auth";

const navItems: Array<{ to: string; label: string; icon: LucideIcon }> = [
  { to: "/accounts", label: "Клиенты", icon: ListTree },
  { to: "/tariffs", label: "Тарифы", icon: Tags },
  { to: "/payment-recipient", label: "Реквизиты получателя", icon: Tags },
  { to: "/documents", label: "Документы", icon: FileText },
  { to: "/security", label: "Безопасность", icon: KeyRound },
  { to: "/settings/prompts", label: "Системные промпты", icon: ListTree },
];

function Brand({ username }: { username: string | undefined }) {
  return (
    <Link to="/accounts" className="flex min-w-0 items-center gap-2">
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary text-primary-foreground">
        <Shield className="h-4 w-4" />
      </span>
      <span className="min-w-0">
        <span className="block truncate text-sm font-semibold">
          WhatsAI Admin
        </span>
        <span className="block truncate text-xs text-muted-foreground">
          {username}
        </span>
      </span>
    </Link>
  );
}

function NavItems({ onNavigate }: { onNavigate?: () => void }) {
  return (
    <nav className="space-y-1">
      {navItems.map((item) => {
        const Icon = item.icon;
        return (
          <NavLink
            key={item.to}
            to={item.to}
            onClick={onNavigate}
            className={({ isActive }) =>
              `flex h-10 items-center gap-3 rounded-md px-3 text-sm font-medium transition-colors ${
                isActive
                  ? "bg-accent text-accent-foreground"
                  : "text-muted-foreground hover:bg-muted hover:text-foreground"
              }`
            }
          >
            <Icon className="h-4 w-4" />
            {item.label}
          </NavLink>
        );
      })}
    </nav>
  );
}

export function AdminLayout() {
  const navigate = useNavigate();
  const { admin, logout } = useAdminAuth();
  const [mobileOpen, setMobileOpen] = useState(false);

  function handleLogout() {
    logout();
    navigate("/login", { replace: true });
  }

  return (
    <div className="admin-shell">
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-72 flex-col border-r bg-card md:flex">
        <div className="border-b p-4">
          <Brand username={admin?.username} />
        </div>
        <div className="flex-1 overflow-y-auto p-3">
          <NavItems />
        </div>
        <div className="border-t p-3">
          <div className="mb-2 px-3">
            <ThemeSelect />
          </div>
          <button
            className="flex h-10 w-full items-center gap-3 rounded-md px-3 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            type="button"
            onClick={handleLogout}
          >
            <LogOut className="h-4 w-4" />
            Выйти
          </button>
        </div>
      </aside>

      <div className="min-h-screen md:pl-72">
        <header className="sticky top-0 z-20 border-b bg-card/94 backdrop-blur md:hidden">
          <div className="flex items-center justify-between gap-3 px-4 py-3">
            <Brand username={admin?.username} />
            <button
              className="inline-flex h-9 w-9 items-center justify-center rounded-md border bg-card"
              type="button"
              aria-label={mobileOpen ? "Закрыть меню" : "Открыть меню"}
              onClick={() => setMobileOpen((value) => !value)}
            >
              {mobileOpen ? (
                <X className="h-4 w-4" />
              ) : (
                <Menu className="h-4 w-4" />
              )}
            </button>
          </div>
          <div className="px-4 pb-3">
            <ThemeSelect />
          </div>
          {mobileOpen ? (
            <div className="border-t px-4 py-3">
              <NavItems onNavigate={() => setMobileOpen(false)} />
              <button
                className="mt-2 flex h-10 w-full items-center gap-3 rounded-md px-3 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                type="button"
                onClick={handleLogout}
              >
                <LogOut className="h-4 w-4" />
                Выйти
              </button>
            </div>
          ) : null}
        </header>

        <main className="mx-auto max-w-7xl px-3 py-4 sm:px-4 sm:py-6 md:px-6 lg:px-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

export function PageHeader({
  title,
  description,
  backTo,
  backLabel = "Назад",
  children,
}: {
  title: string;
  description?: string;
  backTo?: string;
  backLabel?: string;
  children?: ReactNode;
}) {
  return (
    <div className="mb-4 flex flex-col gap-3 sm:mb-6 sm:flex-row sm:items-start sm:justify-between">
      <div className="flex min-w-0 items-start gap-3">
        {backTo ? (
          <Link
            className="mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md border bg-card transition-colors hover:border-primary/40 hover:bg-accent"
            to={backTo}
            aria-label={backLabel}
            title={backLabel}
          >
            <ArrowLeft className="h-4 w-4" />
          </Link>
        ) : null}
        <div className="min-w-0">
          <h1 className="truncate text-xl font-semibold sm:text-2xl">
            {title}
          </h1>
          {description ? (
            <p className="mt-1 text-sm text-muted-foreground">{description}</p>
          ) : null}
        </div>
      </div>
      {children}
    </div>
  );
}
